import { useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { investMalurFeatures } from '@/mocks/landingData';

function AnimatedCounter({ end, suffix = '', prefix = '' }: { end: string; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const numericValue = parseFloat(end.replace(/[^0-9.]/g, ''));
    if (isNaN(numericValue)) return;

    const duration = 2000;
    const steps = 60;
    const increment = numericValue / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current = Math.min(numericValue, increment * step);
      setCount(Number(current.toFixed(numericValue < 10 ? 1 : 0)));
      if (step >= steps) {
        clearInterval(timer);
        setCount(numericValue);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, end]);

  return (
    <div ref={ref} className="font-display text-2xl md:text-3xl lg:text-4xl text-gold font-bold">
      {prefix}{count}{suffix}
    </div>
  );
}

export default function WhyMalur() {
  useScrollReveal('.malur-reveal', 0.1);

  return (
    <section id="malur" className="py-10 md:py-16 bg-cream">
      <div className="section-padding">
        <div className="max-w-3xl mx-auto text-center mb-8 md:mb-12">
          <div className="malur-reveal scroll-reveal">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/10 rounded-full font-body text-[10px] md:text-xs tracking-[0.2em] uppercase text-primary">
              <span className="w-1.5 h-1.5 bg-gold rounded-full" />
              Investment Potential
            </span>
          </div>
          <h2 className="malur-reveal scroll-reveal font-display text-2xl md:text-4xl lg:text-5xl text-charcoal font-bold mt-4 md:mt-5 leading-tight">
            Why Invest In <span className="text-gradient-gold">Malur?</span>
          </h2>
          <p className="malur-reveal scroll-reveal font-body text-sm md:text-base text-charcoal/60 mt-3 md:mt-4 leading-relaxed max-w-2xl mx-auto">
            Malur is emerging as one of the most promising destinations for land investment near Bangalore, offering villa plots in Malur with strong appreciation potential and plotted development Bangalore connectivity. Located on the Bangalore–Chennai growth corridor, Malur benefits from expanding industrial belts, improving road networks, and proximity to major educational institutions like VIT University and Garden City University. With infrastructure projects accelerating and demand for residential land surging, investing in gated community plots in Malur today positions you ahead of tomorrow's price appreciation. Confident Auriga brings premium plots near Bangalore within reach of smart investors seeking long-term capital growth and a peaceful lifestyle.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 max-w-5xl mx-auto">
          {investMalurFeatures.map((feature, index) => (
            <div
              key={feature.title}
              className="malur-reveal scroll-reveal group relative bg-cream rounded-2xl p-5 md:p-6 border border-cream-dark/50 hover:border-gold/30 transition-all duration-500 hover:-translate-y-1 overflow-hidden"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="absolute top-4 right-4 md:top-5 md:right-5 text-right">
                <AnimatedCounter end={feature.stat} />
                <p className="font-body text-[10px] text-charcoal/50 tracking-wider uppercase mt-0.5">{feature.statLabel}</p>
              </div>
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-primary/5 flex items-center justify-center mb-3 md:mb-4 group-hover:bg-gold/10 transition-colors duration-500">
                <i className={`${feature.icon} text-primary text-lg md:text-xl group-hover:text-gold transition-colors duration-500`}></i>
              </div>
              <h3 className="font-display text-base md:text-lg text-charcoal font-semibold mb-1.5 pr-14">{feature.title}</h3>
              <p className="font-body text-xs text-charcoal/60 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}